import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const PAGE_PATH = '/add-your-brand';
const PAGE_TITLE = 'Add Your Brand | List Your Store on SavingArena';
const PAGE_DESCRIPTION =
  'Get your store in front of millions of deal-seeking shoppers. Submit your brand to SavingArena and start publishing verified coupons and offers.';

@Component({
  selector: 'app-add-your-brand-page',
  imports: [Breadcrumb, ReactiveFormsModule, RouterLink, DevFlag],
  templateUrl: './add-your-brand-page.html',
  styleUrl: './add-your-brand-page.scss',
})
export class AddYourBrandPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'Add Your Brand' });

  private readonly formBuilder = inject(FormBuilder);
  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);

  // Accepts an optional protocol, requires a dotted host — the server re-validates authoritatively.
  private readonly urlPattern = /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i;

  protected readonly form = this.formBuilder.group({
    storeName: ['', [Validators.required]],
    websiteUrl: ['', [Validators.required, Validators.pattern(this.urlPattern)]],
    contactName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    category: [''],
    country: [''],
    description: [''],
    hasAffiliateProgram: ['false'],
    affiliateNetwork: [''],
    commissionDetails: [''],
    couponDealDetails: [''],
    notes: [''],
    // Honeypot — hidden from real users; bots fill it and the server silently drops the submit.
    companyWebsite: [''],
  });

  protected readonly submitting = signal(false);
  protected readonly submitted = signal(false);
  protected readonly submitError = signal('');
  protected readonly logoError = signal('');
  protected readonly logoName = signal('');
  private logoFile: File | null = null;

  constructor() {
    // Fully static form page — the constructor is the right place for its tags.
    const url = this.seo.absolute(PAGE_PATH);

    this.seo.apply({
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      canonicalPath: PAGE_PATH,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  protected onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.logoError.set('');

    if (!file) {
      this.logoFile = null;
      this.logoName.set('');
      return;
    }

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      this.logoError.set('Logo must be a PNG, JPG, JPEG or WEBP image.');
      this.clearLogo(input);
      return;
    }

    if (file.size > MAX_LOGO_BYTES) {
      this.logoError.set('Logo must be 2MB or smaller.');
      this.clearLogo(input);
      return;
    }

    this.logoFile = file;
    this.logoName.set(file.name);
  }

  protected submitForm(): void {
    this.submitError.set('');

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = new FormData();
    for (const [key, value] of Object.entries(this.form.getRawValue())) {
      payload.append(key, value == null ? '' : String(value));
    }
    if (this.logoFile) {
      payload.append('logo', this.logoFile);
    }

    this.submitting.set(true);
    this.api
      .submitStoreRequest(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitted.set(true);
          if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        },
        error: () => {
          this.submitting.set(false);
          this.submitError.set('Something went wrong while submitting. Please try again.');
        },
      });
  }

  protected isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  private clearLogo(input: HTMLInputElement): void {
    this.logoFile = null;
    this.logoName.set('');
    input.value = '';
  }
}
